/**
 * Contrat de campagne — « Qu'avez-vous promis pendant votre campagne ? »
 *
 * Overlay plein écran à T5 (après que le joueur a goûté au jeu, avant la
 * cristallisation du profil T8). Le joueur n'invente pas un objectif : il
 * ASSUME une promesse qui préexiste — le pays s'en souvient, l'opposition
 * aussi, et la trahir peut coûter le mandat.
 *
 * Le choix (1 à 2 promesses) est injecté dans les choices du prochain
 * tour (`pledge_*`) : il vit dans l'historique de décisions → replay-safe.
 * Gate : `turn >= 5 && pledges vides && pendingPledges vides`.
 */

import { useState } from "react";
import { useGame } from "../store/useGame";
import { PLEDGE_CARDS } from "../engine/pledges";

const TRIGGER_TURN = 5;
const MAX_CHOICES = 2;

export function PledgeOverlay() {
  const turn = useGame((s) => s.worldState?.turn ?? 0);
  const mandateEnded = useGame(
    (s) => s.worldState?.polity.mandate_ended ?? false,
  );
  const pledges = useGame((s) => s.pledges);
  const pendingPledges = useGame((s) => s.pendingPledges);
  const choosePledges = useGame((s) => s.choosePledges);

  const [selected, setSelected] = useState<string[]>([]);
  const [confirmed, setConfirmed] = useState(false);

  const due =
    turn >= TRIGGER_TURN &&
    !mandateEnded &&
    pledges.length === 0 &&
    pendingPledges.length === 0;
  if (!due) return null;

  const toggle = (id: string) => {
    setSelected((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : prev.length < MAX_CHOICES
          ? [...prev, id]
          : prev,
    );
  };

  const validate = () => {
    if (selected.length === 0) return;
    setConfirmed(true);
  };

  const commit = () => {
    choosePledges(selected);
  };

  if (confirmed) {
    const chosen = PLEDGE_CARDS.filter((c) => selected.includes(c.id));
    return (
      <div className="pledge-backdrop" role="dialog" aria-modal="true">
        <div className="pledge-card">
          <div className="pledge-kicker">LE CONTRAT EST SIGNÉ</div>
          <h2 className="pledge-title">Le pays s'en souvient.</h2>
          <div className="pledge-chosen">
            {chosen.map((c) => (
              <div key={c.id} className="pledge-chosen-item">
                <strong>{c.label}</strong>
                <em>{c.slogan}</em>
              </div>
            ))}
          </div>
          <p className="pledge-warning">
            Ceux qui y ont cru vous font crédit — pour l'instant. La presse
            rappellera votre promesse à chaque bilan. L'opposition attend le
            faux pas. Et un peuple trahi ne pardonne pas toujours dans les
            urnes&nbsp;: parfois, il descend dans la rue.
          </p>
          <button type="button" className="pledge-validate" onClick={commit}>
            ━━ GOUVERNER ━━
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pledge-backdrop" role="dialog" aria-modal="true">
      <div className="pledge-card">
        <div className="pledge-kicker">SOUVENEZ-VOUS · LA CAMPAGNE</div>
        <h2 className="pledge-title">
          Qu'avez-vous promis pendant votre campagne&nbsp;?
        </h2>
        <p className="pledge-hint">
          Les meetings, les plateaux, les tracts — vous avez promis. Assumez une
          ou deux promesses&nbsp;: elles deviennent votre contrat avec le pays.
        </p>
        <div className="pledge-grid">
          {PLEDGE_CARDS.map((c) => {
            const isSelected = selected.includes(c.id);
            return (
              <button
                key={c.id}
                type="button"
                className={`pledge-option ${isSelected ? "pledge-option-selected" : ""}`}
                aria-pressed={isSelected}
                onClick={() => toggle(c.id)}
              >
                <span className="pledge-option-label">{c.label}</span>
                <span className="pledge-option-slogan">{c.slogan}</span>
                <span className="pledge-option-measure">{c.measure}</span>
              </button>
            );
          })}
        </div>
        <button
          type="button"
          className="pledge-validate"
          onClick={validate}
          disabled={selected.length === 0}
        >
          ━━ ASSUMER {selected.length <= 1 ? "CETTE PROMESSE" : "CES PROMESSES"}{" "}
          ━━
        </button>
      </div>
    </div>
  );
}
